import React, { useState } from "react";

import "./artifacts.css";

// The Accountability Split. Authority carries weight, so accountability is not
// shared equally. The split is convex, not linear: a senior role holds
// disproportionately more, and the convexity grows with headcount, so you
// cannot offload a VP's ownership onto three developers. Removing a player
// pushes their share onto the people who remain. Exports a NIST GOVERN 2.1
// record with project, deadline, risks, and stakeholders in one place.

const NIST_GOVERN_21 =
	'NIST AI RMF GOVERN 2.1: "Roles and responsibilities and lines of communication related to mapping, measuring, and managing AI risks are documented and are clear to individuals and teams throughout the organization." A documented accountability split is exactly that.';

const ROSTER = [
	{ key: "vp", role: "VP / Exec", weight: 5, note: "owns the outcome" },
	{ key: "dir", role: "Director", weight: 4, note: "owns the call" },
	{ key: "em", role: "Engineering Manager", weight: 3, note: "owns delivery" },
	{ key: "arch", role: "Architect", weight: 2.5, note: "owns the design" },
	{ key: "sre", role: "Senior Engineer", weight: 2, note: "owns the build" },
	{ key: "dev", role: "Developer", weight: 1.5, note: "does the work" },
	{ key: "ba", role: "Business Analyst", weight: 1, note: "shapes the ask" },
];

const DEFAULT_SELECTED = ["vp", "dir", "sre", "dev"];

// Healthy accountability band per role, as a share of the total. Over the
// ceiling = over-burdened (or a single point of failure at the top). Under the
// floor = under-owned (usually a sign the group is spread too thin).
const BANDS = {
	vp: [35, 70],
	dir: [20, 55],
	em: [15, 45],
	arch: [10, 35],
	sre: [8, 28],
	dev: [5, 20],
	ba: [3, 15],
};

const statusOf = (key, pct) => {
	const [min, max] = BANDS[key];
	if (pct > max) return "over";
	if (pct < min) return "under";
	return "ok";
};

// Convex split. Exponent rises with headcount so seniority concentrates as the
// group grows. pct_i proportional to weight_i ^ p.
const computeSplit = (sel) => {
	const chosen = ROSTER.filter((r) => sel.has(r.key));
	const n = chosen.length;
	const p = 1 + (n - 1) * 0.15;
	const raw = chosen.map((r) => ({ ...r, w: Math.pow(r.weight, p) }));
	const totalW = raw.reduce((s, r) => s + r.w, 0) || 1;
	const arr = raw
		.map((r) => ({
			key: r.key,
			role: r.role,
			note: r.note,
			weight: r.weight,
			pct: Math.round((r.w / totalW) * 100),
		}))
		.sort((a, b) => b.weight - a.weight);
	// Absorb rounding remainder into the top holder so it always sums to 100.
	const sum = arr.reduce((s, r) => s + r.pct, 0);
	if (arr.length) arr[0].pct += 100 - sum;
	return arr;
};

const AccountabilitySplit = () => {
	const [selected, setSelected] = useState(() => new Set(DEFAULT_SELECTED));
	const [deltas, setDeltas] = useState({});
	const [project, setProject] = useState("");
	const [deadline, setDeadline] = useState("");
	const [risks, setRisks] = useState("");
	const [stakeholders, setStakeholders] = useState("");

	const toggle = (key) => {
		const before = computeSplit(selected);
		const beforeMap = Object.fromEntries(before.map((r) => [r.key, r.pct]));
		const next = new Set(selected);
		if (next.has(key)) next.delete(key);
		else next.add(key);
		const after = computeSplit(next);
		const d = {};
		after.forEach((r) => {
			d[r.key] = r.pct - (beforeMap[r.key] ?? r.pct);
		});
		setSelected(next);
		setDeltas(d);
	};

	const split = computeSplit(selected).map((r) => ({
		...r,
		status: statusOf(r.key, r.pct),
	}));
	const chosenCount = split.length;
	const top = split[0];
	const hasSenior = split.some((r) => r.weight >= 3); // EM or above
	const overloaded = split.filter((r) => r.status === "over");

	const verdict = () => {
		if (chosenCount === 0)
			return { tone: "under", text: "Pick the people in this decision to see if the split is healthy." };
		if (!hasSenior)
			return {
				tone: "over",
				text: "No senior owner. No manager or above is on the hook for this decision. Add one before it ships.",
			};
		if (overloaded.length)
			return {
				tone: "over",
				text: `${overloaded.map((r) => r.role).join(" and ")} ${
					overloaded.length > 1 ? "are" : "is"
				} carrying more than a healthy load. This split is unbalanced, add a senior owner or trim the group.`,
			};
		const under = split.filter((r) => r.status === "under");
		if (under.length)
			return {
				tone: "under",
				text: `${under
					.map((r) => r.role)
					.join(", ")} ${
					under.length > 1 ? "hold" : "holds"
				} less than a healthy share. You may have spread this too thin.`,
			};
		return {
			tone: "ok",
			text: "Balanced. Every role sits within a healthy accountability range.",
		};
	};
	const v = verdict();

	const insight = () => {
		if (chosenCount === 0)
			return "Pick at least one person to see how the accountability splits.";
		if (chosenCount === 1)
			return `${top.role} carries 100 percent. Total clarity, and a single point of failure.`;
		if (chosenCount >= 5)
			return `Across ${chosenCount} people the curve steepens: ${top.role} still holds ${top.pct} percent while the rest thin out. Adding names feels like sharing the risk, but authority keeps most of it at the top.`;
		return `${top.role} holds ${top.pct} percent. Accountability is not linear, authority bends the curve. Drop a name and the others absorb it; add one and it dilutes.`;
	};

	const downloadPdf = async () => {
		const { jsPDF } = await import("jspdf");
		const doc = new jsPDF({ unit: "pt", format: "letter" });
		const M = 54;
		const Wd = 612 - M * 2;
		let y = 64;
		const write = (text, size, bold, color) => {
			doc.setFont("helvetica", bold ? "bold" : "normal");
			doc.setFontSize(size);
			doc.setTextColor(color[0], color[1], color[2]);
			const lines = doc.splitTextToSize(text, Wd);
			doc.text(lines, M, y);
			y += lines.length * (size + 4);
		};
		const gap = (h) => {
			y += h;
		};

		write("The Accountability Split", 20, true, [26, 32, 44]);
		write(project.trim() || "Weighted by role authority", 13, false, [
			90, 90, 90,
		]);
		gap(4);
		write(
			"NIST AI RMF GOVERN 2.1 accountability record",
			10,
			true,
			[49, 130, 206]
		);
		write(
			"Generated " +
				new Date().toLocaleDateString("en-US", {
					year: "numeric",
					month: "long",
					day: "numeric",
				}),
			9,
			false,
			[130, 130, 130]
		);
		if (deadline)
			write("Decision deadline: " + deadline, 9, false, [130, 130, 130]);
		gap(14);
		write(
			`Accountability split across ${chosenCount} ${
				chosenCount === 1 ? "person" : "people"
			}`,
			13,
			true,
			[40, 40, 40]
		);
		gap(8);
		const STATUS_COLOR = {
			ok: [47, 133, 90],
			under: [221, 107, 32],
			over: [197, 48, 48],
		};
		split.forEach((r) =>
			write(`•  ${r.role}:  ${r.pct}%`, 12, false, STATUS_COLOR[r.status])
		);
		gap(12);
		// Color legend
		const swatches = [
			{ c: STATUS_COLOR.ok, label: "Healthy range" },
			{ c: STATUS_COLOR.under, label: "Under-owned" },
			{ c: STATUS_COLOR.over, label: "Over-burdened" },
		];
		let lx = M;
		doc.setFont("helvetica", "normal");
		doc.setFontSize(9);
		swatches.forEach((s) => {
			doc.setFillColor(s.c[0], s.c[1], s.c[2]);
			doc.rect(lx, y - 8, 9, 9, "F");
			doc.setTextColor(90, 90, 90);
			doc.text(s.label, lx + 13, y);
			lx += 13 + doc.getTextWidth(s.label) + 20;
		});
		gap(14);
		write(v.text, 11, true, STATUS_COLOR[v.tone]);
		gap(4);
		write(insight(), 10, false, [90, 90, 90]);
		if (risks.trim()) {
			gap(12);
			write("RISKS & DEPENDENCIES", 10, true, [113, 128, 150]);
			write(risks.trim(), 11, false, [40, 40, 40]);
		}
		if (stakeholders.trim()) {
			gap(12);
			write("STAKEHOLDERS TO INFORM", 10, true, [113, 128, 150]);
			write(stakeholders.trim(), 11, false, [40, 40, 40]);
		}
		gap(14);
		write(NIST_GOVERN_21, 9, false, [110, 110, 110]);
		gap(8);
		write(
			"Generated with The Accountability Split at swapniltamse.com",
			8,
			false,
			[150, 150, 150]
		);
		doc.save("accountability-split.pdf");
	};

	return (
		<div className="artifact-card">
			<div className="artifact-kicker">Interactive · Free tool</div>
			<div className="artifact-title">The Accountability Split</div>
			<div className="artifact-intro">
				Accountability is not shared equally, and it is not linear. Whoever
				holds more authority holds disproportionately more of the outcome.
				Pick the people in a decision and see how it actually splits. Fewer
				names concentrate it, more names dilute it, and a VP always
				outweighs a developer or a BA.
			</div>

			<div className="split-fields">
				<div className="split-field">
					<label>Project or decision (optional)</label>
					<input
						type="text"
						placeholder="e.g. Roll out the fraud model to prod"
						value={project}
						onChange={(e) => setProject(e.target.value)}
					/>
				</div>
				<div className="split-field split-field-sm">
					<label>Deadline (optional)</label>
					<input
						type="date"
						value={deadline}
						onChange={(e) => setDeadline(e.target.value)}
					/>
				</div>
			</div>

			<div className="split-roster">
				{ROSTER.map((r) => {
					const on = selected.has(r.key);
					const d = on ? deltas[r.key] : undefined;
					return (
						<label
							className={`split-role${on ? " on" : ""}`}
							key={r.key}
						>
							<input
								type="checkbox"
								checked={on}
								onChange={() => toggle(r.key)}
							/>
							<span className="split-role-name">{r.role}</span>
							<span className="split-role-note">{r.note}</span>
							{d ? (
								<span
									key={d}
									className={`split-role-delta${
										d > 0 ? " up" : " down"
									}`}
								>
									{d > 0 ? `+${d}%` : `${d}%`}
								</span>
							) : null}
							<span className="split-role-weight">
								weight {r.weight}
							</span>
						</label>
					);
				})}
			</div>

			{split.length > 0 && (
				<div className="split-bars">
					<div className="split-bars-title">
						Accountability split ({chosenCount}{" "}
						{chosenCount === 1 ? "person" : "people"})
					</div>
					{split.map((r) => (
						<div className="split-bar-row" key={r.key}>
							<span className="split-bar-label">{r.role}</span>
							<div className="split-bar-track">
								<div
									className={`split-bar-fill status-${r.status}`}
									style={{ width: `${r.pct}%` }}
								/>
							</div>
							<span className="split-bar-pct">{r.pct}%</span>
						</div>
					))}
					<div className="split-legend">
						<span>
							<i className="split-dot status-ok" /> Healthy range
						</span>
						<span>
							<i className="split-dot status-under" /> Under-owned
						</span>
						<span>
							<i className="split-dot status-over" /> Over-burdened
						</span>
					</div>
				</div>
			)}

			<div className="artifact-result">
				<div
					className="artifact-readout"
					style={{
						color:
							v.tone === "ok"
								? "#2f855a"
								: v.tone === "over"
								? "#c53030"
								: "#dd6b20",
					}}
				>
					<strong>{v.text}</strong>
				</div>
				<div className="split-subnote">{insight()}</div>

				<div className="split-fields split-fields-stack">
					<div className="split-field">
						<label>Risks & dependencies (optional)</label>
						<textarea
							rows="2"
							placeholder="What could go wrong, and what this depends on"
							value={risks}
							onChange={(e) => setRisks(e.target.value)}
						/>
					</div>
					<div className="split-field">
						<label>Stakeholders to inform (optional)</label>
						<textarea
							rows="2"
							placeholder="Who needs to know the outcome"
							value={stakeholders}
							onChange={(e) => setStakeholders(e.target.value)}
						/>
					</div>
				</div>

				<div className="chain-nist">
					Aligned with <b>NIST AI RMF GOVERN 2.1</b>: roles and
					responsibilities documented and clear across the AI lifecycle. A
					written accountability split is that record.
				</div>

				<div className="chain-actions">
					<button
						className="artifact-btn"
						type="button"
						onClick={downloadPdf}
						disabled={chosenCount === 0}
					>
						Download PDF record
					</button>
				</div>
			</div>
		</div>
	);
};

export default AccountabilitySplit;
